import { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'

import { Typography } from '@mui/material'

import moment from 'moment-timezone'
import _ from 'lodash'

import ManagePassSubpageLayout from '/components/passes/manage-pass-subpage-layout'
import { AppContext } from '/providers/app-context'
import HelpText from '/help/pass-details'
import Preview from '/components/passes/preview'
import Summary from '/components/passes/summary'

import { getPass } from '/providers/passes-api'

const PassDetails = () => {

  const navigate = useNavigate()
  const location = useLocation()

  const { templateId, passId } = useParams()
  const { apiSource, apiKey, templates, passes, setPasses } = useContext(AppContext)

  const [pass, setPass] = useState(() => location.state?.pass ?? passes.find(item => item.template_id === templateId && item.pass_id === passId))

  const template = useMemo(() => { return templates.filter(t => t.template_id === templateId)[0] }, [templates, templateId])

  useEffect(() => {
    const newPass = passes.filter(p => p.template_id === templateId && p.pass_id === passId)[0]
    if (newPass) setPass(newPass)
  }, [passId, passes, templateId])

  const getPassData = useCallback(async () => {
    const p = await getPass({ apiSource, apiKey, passId })
    if (p.ok === false || !p.pass) return navigate("/templates")
    setPasses(current => {
      const idx = current.findIndex(item => item.template_id === templateId && item.pass_id === passId)
      if (idx < 0) return [...current, p.pass]
      const next = [...current]
      next[idx] = p.pass
      return next
    })
    setPass(p.pass)
  }, [apiKey, apiSource, navigate, passId, setPasses, templateId])

  useEffect(() => {
    getPassData()
  }, [getPassData])

  return (
    <ManagePassSubpageLayout title={`Pass Details (${pass?.pass_id})`} HelpText={HelpText} heading={`${pass?.pass_id} ${pass?.mode === "test" ? " [test]" : ""}`} template={template} pass={pass} onClose={() => navigate(`/templates/${templateId}/passes`)}>
      <Content template={template} pass={pass} setPass={setPass} />
    </ManagePassSubpageLayout>
  )
}

export default PassDetails

const Content = ({ template, pass, setPass }) => {
  if (template && pass) return (
    <div className='flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden'>
      <div className='flex min-h-0 w-full min-w-0 flex-1 flex-wrap content-start items-start gap-5 overflow-y-auto p-2 pb-10'>
        <div className='min-w-0 max-w-[600px] flex-[1_1_600px]'>
          <Summary template={template} pass={pass} setPass={setPass} classes="mb-5 w-full" />
          <SmartixScannerPassInfo template={template} pass={pass} />
        </div>
        <div className='min-w-[760px] flex-[1_1_760px] overflow-x-auto'>
          <Preview template={template} pass={pass} classes="min-w-[760px]" />
        </div>
      </div>
    </div>
  )
}

const SmartixScannerPassInfo = ({ template, pass }) => {

  if (!template.managed_pass_template) return null

  const passWithEffectiveVariables = {
    ...pass,
    variables: _.merge({}, template.variables, pass.variables),
  }

  switch (template.pass_type_id) {
    case "managed_stamp": return <SmartixStamp pass={passWithEffectiveVariables} />
    case "managed_gift": return <SmartixGift pass={passWithEffectiveVariables} />
    case "managed_loyalty": return <SmartixLoyalty pass={passWithEffectiveVariables} />
    case "managed_event": return <SmartixEvent pass={passWithEffectiveVariables} />
    case "managed_event_bg": return <SmartixEvent pass={passWithEffectiveVariables} />
  }
}

const SmartixStamp = ({ pass }) => {

  const lastCollection = pass.scanner_audit_data_enc.filter(i => i.action === "collect")[0]
  const lastRedemption = pass.scanner_audit_data_enc.filter(i => i.action === "redeem")[0]

  return (
    <div className='flex flex-col gap-2 p-2'>
      <Typography variant='h3'>Stampcard details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatUtcDate(pass.created_date)}</div></div>

      <div className='flex flex-row justify-between'><div>Current Stamps</div><div>{pass.variables.stamp.stamps.balance}</div></div>
      <div className='flex flex-row justify-between'><div>Current Full Cards</div><div>{pass.variables.stamp.cards.balance}</div></div>

      <div className='flex flex-row justify-between'><div>Full Cards Redeemed</div><div>{pass.variables.stamp.cards.redeemed}</div></div>

      <div className='flex flex-row justify-between'><div>Total Stamps Collected</div><div>{pass.variables.stamp.stamps.collected}</div></div>

      <div className='flex flex-row justify-between'><div>Last Collection</div><div>{lastCollection ? `${formatDate(lastCollection.timestamp)} : ${lastCollection.stamps} stamps` : "-"}</div></div>
      <div className='flex flex-row justify-between'><div>Last Redemption</div><div>{lastRedemption ? `${formatDate(lastRedemption.timestamp)} : ${lastRedemption.cards} cards` : "-"}</div></div>

    </div>
  )
}

const SmartixGift = ({ pass }) => {

  const lastBalanceChange = pass.scanner_audit_data_enc.filter(i => ["debit", "refund", "credit"].includes(i.action)).pop()
  const lastUsedDate = lastBalanceChange?.timestamp

  return (
    <div className='flex flex-col gap-2 ml-20 mr-20 pt-10 pb-20'>
      <Typography variant='h3'>Giftcard details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatUtcDate(pass.created_date)}</div></div>

      <div className='flex flex-row justify-between'><div>Gift card amount</div><div>{formatMoney(pass.variables.gift.gifted_amount,pass.variables.gift.currency_code)}</div></div>
      <div className='flex flex-row justify-between'><div>Gift card balance</div><div>{formatMoney(pass.variables.gift.balance,pass.variables.gift.currency_code)}</div></div>
      <div className='flex flex-row justify-between'><div>Expires</div><div>{pass.expiry_date && moment.utc(pass.expiry_date).isAfter(moment.utc()) ? formatUtcDate(pass.expiry_date) : `Giftcard expired ${formatUtcDate(pass.expiry_date)}`}</div></div>

      <div className='flex flex-row justify-between'><div>Last used</div><div>{lastUsedDate ? formatDate(lastUsedDate) : "Not used"}</div></div>

    </div>
  )
}

const SmartixLoyalty = ({ pass }) => {

  const lastBalanceChange = pass.scanner_audit_data_enc.filter(i => ["debit", "refund", "credit"].includes(i.action)).pop()
  const lastUsedDate = lastBalanceChange?.timestamp

  return (
    <div className='flex flex-col gap-2 ml-20 mr-20 pt-10 pb-20'>
      <Typography variant='h3'>Loyalty card details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatUtcDate(pass.created_date)}</div></div>

      <div className='flex flex-row justify-between'><div>Points balance</div><div>{pass.variables.loyalty.points.balance}</div></div>
      <div className='flex flex-row justify-between'><div>Expires</div><div>{pass.expiry_date ? moment.utc(pass.expiry_date).isAfter(moment.utc()) ? formatUtcDate(pass.expiry_date) : `Giftcard expired ${formatUtcDate(pass.expiry_date)}` : '-'}</div></div>

      <div className='flex flex-row justify-between'><div>Last used</div><div>{lastUsedDate ? formatDate(lastUsedDate) : "Not used"}</div></div>

    </div>
  )
}

const SmartixEvent = ({ pass }) => {
  return (
    <div className='flex flex-col gap-2 ml-20 mr-20 pt-10 pb-20'>
      <Typography variant='h3'>Event Ticket details</Typography>

      <div className='flex flex-row justify-between'><div>Issued</div><div>{formatUtcDate(pass.created_date)}</div></div>

      <div className='flex flex-row justify-between'><div>Used</div><div>{pass.variables.event.ticket.scanned > 0 ? "Yes" : "No"}</div></div>
    </div>
  )
}


const formatDate = date => moment(date, "YYYYMMDDHHmmSS").format("DD MMM YYYY")
const formatDateTime = date => moment(date, "YYYYMMDDHHmmSS").format("DD MMM YYYY HH:mm")
const formatUtcDate = date => date ? moment.utc(date).tz('Europe/London').format("DD MMM YYYY") : '-'
const formatMoney = (amount,currency) => amount.toLocaleString('en-GB', {style: 'currency', currency})